import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  useTheme,
  IconButton,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Save,
  Lock,
  Email,
  Security,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

interface CredentialManagementProps {
  onNotification: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void;
}

const CredentialManagement: React.FC<CredentialManagementProps> = ({ onNotification }) => {
  const theme = useTheme();
  const { user, updateCredentials } = useAuth();
  const [newEmail, setNewEmail] = useState(user?.email || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Update email when user changes
  React.useEffect(() => {
    if (user?.email) {
      setNewEmail(user.email);
    }
  }, [user]);

  const handleSave = async () => {
    if (!currentPassword) {
      onNotification('Please enter your current password', 'error');
      return;
    }

    // Check if at least one field is being updated
    if (!newEmail && !newPassword) {
      onNotification('Please provide at least one field to update', 'error');
      return;
    }

    // If password is being changed, verify confirmation
    if (newPassword && newPassword !== confirmPassword) {
      onNotification('New passwords do not match', 'error');
      return;
    }

    if (newPassword && newPassword.length < 8) {
      onNotification('Password must be at least 8 characters long', 'error');
      return;
    }

    setLoading(true);
    try {
      const result = await updateCredentials(
        currentPassword,
        newEmail !== user?.email ? newEmail : undefined,
        newPassword || undefined
      );
      
      if (result.success) {
        setNewPassword('');
        setConfirmPassword('');
        setCurrentPassword('');
        onNotification('Credentials updated successfully!', 'success');
      } else {
        onNotification(result.error || 'Failed to update credentials', 'error');
      }
    } catch (error) {
      onNotification('Failed to update credentials', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setNewEmail(user?.email || '');
    setNewPassword('');
    setConfirmPassword('');
    setCurrentPassword('');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Security sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
        <Typography variant="h5" component="h2" color="primary" sx={{ fontWeight: 600 }}>
          Security Settings
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ p: 4, borderRadius: 4 }}>
        <Typography variant="h6" gutterBottom color="text.primary">
          Change Admin Credentials
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Update your admin email and password. You'll need to enter your current password to make changes.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: 'grid', gap: 3 }}>
          {/* Current Credentials Display */}
          <Box>
            <Typography variant="subtitle1" gutterBottom color="text.primary">
              Current Credentials
            </Typography>
            <TextField
              label="Current Email"
              value={user?.email || ''}
              disabled
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* New Credentials Input */}
          <Box>
            <Typography variant="subtitle1" gutterBottom color="text.primary">
              New Credentials
            </Typography>
            
            <TextField
              fullWidth
              label="New Email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Current Password Verification */}
          <Box>
            <Typography variant="subtitle1" gutterBottom color="text.primary">
              Verify Current Password
            </Typography>
            <TextField
              fullWidth
              label="Enter Current Password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={loading || !newEmail || !newPassword || !confirmPassword || !currentPassword}
            sx={{
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
              '&:hover': {
                background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark} 90%)`,
              },
            }}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleReset}
            disabled={loading}
          >
            Reset
          </Button>
        </Box>

        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Security Note:</strong> After changing your credentials, you'll need to log in again with the new credentials. 
            Make sure to remember your new password as it cannot be recovered.
          </Typography>
        </Alert>
      </Paper>
    </Box>
  );
};

export default CredentialManagement;


